/**
 * SUBSCRIPTION WRAPPER FOR AGRI ADMIN APP
 *
 * SIMPLE INSTALLATION:
 * 1. Save this as: SubscriptionWrapper.js
 * 2. In your index.js or App.js, import this instead of AgriAdmin
 * 3. This wraps your existing app with subscription checking
 *
 * NO CHANGES NEEDED TO YOUR EXISTING AGRI ADMIN APP!
 */

import React, { useState, useEffect } from 'react';
import AgriAdmin from './App'; // Your existing app

const WIX_API_URL = 'https://www.agri-admin.au/_functions/checkAccess';

async function checkSubscription(email) {
  try {
    const response = await fetch(WIX_API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.toLowerCase() })
    });

    if (!response.ok) throw new Error('Network error');
    const data = await response.json();
    return data;

  } catch (error) {
    console.error('Subscription check failed:', error);
    
    // Offline or server unreachable — check cache.
    // There is no cache age limit here. A farmer with a valid subscription
    // should never be locked out simply because they are offline or haven't
    // opened the app in months. Lockout only happens on a confirmed server
    // response of valid: false.
    const cached = localStorage.getItem('subscriptionCache');
    if (cached) {
      try {
        const cache = JSON.parse(cached);
        if (cache.email.toLowerCase() === email.toLowerCase() && cache.status !== 'cancelled') {
          return { valid: true, status: cache.status, expiryDate: cache.expiryDate, message: 'Valid (offline)' };
        }
      } catch (e) {
        console.error('Cache parse error:', e);
      }
    }
    
    // No usable cache — cannot verify
    return { valid: false, status: 'unknown', message: 'Cannot verify subscription. Please connect to the internet.' };
  }
}

export default function SubscriptionWrapper() {
  const [userEmail, setUserEmail] = useState(localStorage.getItem('userEmail') || '');
  const [setupFarmerName, setSetupFarmerName] = useState('');
  const [setupPropertyAddress, setSetupPropertyAddress] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(() => {
    // Initialise from cache to prevent flash of full app for expired users
    const cached = localStorage.getItem('subscriptionCache');
    if (cached) {
      try {
        const cache = JSON.parse(cached);
        const storedEmail = (localStorage.getItem('userEmail') || '').toLowerCase();
        if (storedEmail && cache.email === storedEmail) {
          let isValid = cache.status === 'active' || cache.status === 'trial';
          if (isValid && cache.expiryDate) {
            if (new Date(cache.expiryDate) < new Date()) {
              isValid = false;
            }
          }
          return { valid: isValid, status: isValid ? cache.status : 'expired', expiryDate: cache.expiryDate, message: 'Cached' };
        }
      } catch (e) { /* ignore corrupt cache */ }
    }
    return null;
  });
  const [isValidating, setIsValidating] = useState(false);
  const [isSetupComplete, setIsSetupComplete] = useState(
    localStorage.getItem('setupComplete') === 'true' && localStorage.getItem('userEmail')
  );

  // Check subscription on load — re-validates daily when online
  useEffect(() => {
    if (isSetupComplete && userEmail) {
      const lastValidated = localStorage.getItem('lastSubscriptionCheck');
      const oneDayMs = 24 * 60 * 60 * 1000;
      const needsRecheck = !lastValidated || (Date.now() - parseInt(lastValidated, 10)) > oneDayMs;
      if (needsRecheck) {
        validateSubscription().then(function(result) {
          // Only record a successful check time if we actually reached the server
          if (result && result.status !== 'unknown') {
            localStorage.setItem('lastSubscriptionCheck', String(Date.now()));
          }
        });
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSetupComplete, userEmail]);

  const validateSubscription = async () => {
    if (!userEmail) return null;

    setIsValidating(true);
    let validationResult = null;
    try {
      const result = await checkSubscription(userEmail);
      validationResult = result;

      // Only update subscription status if we got a real server response.
      // If offline (status === 'unknown'), don't overwrite a previously valid
      // status — the user should not be locked out just because they're offline.
      if (result.status !== 'unknown') {
        setSubscriptionStatus(result);
      } else if (subscriptionStatus === null) {
        // First load and offline — set status so app can render
        setSubscriptionStatus(result);
      }

      if (result.valid) {
        const cache = {
          email: userEmail.toLowerCase(),
          status: result.status,
          expiryDate: result.expiryDate,
          lastChecked: Date.now()
        };
        localStorage.setItem('subscriptionCache', JSON.stringify(cache));
      } else if (result.status && result.status !== 'unknown') {
        // Server confirmed invalid — cache the cancelled/expired status so
        // offline fallback won't grant access on next offline open
        const cache = {
          email: userEmail.toLowerCase(),
          status: result.status,
          expiryDate: result.expiryDate || null,
          lastChecked: Date.now()
        };
        localStorage.setItem('subscriptionCache', JSON.stringify(cache));
      }
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setIsValidating(false);
    }
    return validationResult;
  };

  const completeSetup = async () => {
    if (!setupFarmerName || !setupPropertyAddress || !userEmail) {
      alert('Please fill in all fields');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(userEmail)) {
      alert('Please enter a valid email');
      return;
    }

    setIsValidating(true);
    
    try {
      const result = await checkSubscription(userEmail);
      
      if (!result.valid) {
        const isOffline = result.status === 'unknown';
        alert(
          isOffline
            ? 'Cannot verify subscription — check your internet connection and try again.'
            : 'No active subscription found for this email.\n\n' + result.message + '\n\nSubscribe at: agri-admin.au'
        );
        setIsValidating(false);
        return;
      }

      // Save and continue
      localStorage.setItem('defaultFarmerName', setupFarmerName);
      localStorage.setItem('propertyAddress', setupPropertyAddress);
      localStorage.setItem('userEmail', userEmail);
      localStorage.setItem('setupComplete', 'true');
      
      setSubscriptionStatus(result);
      setIsSetupComplete(true);

    } catch (error) {
      alert('Unable to verify. Check internet connection.');
    } finally {
      setIsValidating(false);
    }
  };

  // Setup screen
  if (!isSetupComplete) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-lg shadow-2xl p-8">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">🧪</div>
              <h1 className="text-3xl font-bold text-green-800 mb-2">Agri Admin</h1>
              <p className="text-gray-600">Set up your account</p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Name *</label>
                <input
                  type="text"
                  value={setupFarmerName}
                  onChange={(e) => setSetupFarmerName(e.target.value)}
                  placeholder="John Smith"
                  className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Email * (Subscription Email)</label>
                <input
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="your.email@farm.com.au"
                  className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg"
                />
                <p className="text-xs text-gray-500 mt-1">Use the email you subscribed with</p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Property Address *</label>
                <input
                  type="text"
                  value={setupPropertyAddress}
                  onChange={(e) => setSetupPropertyAddress(e.target.value)}
                  placeholder="123 Farm Road, Wagga NSW"
                  className="w-full p-4 border-2 border-gray-300 rounded-lg text-lg"
                />
              </div>

              <button
                onClick={completeSetup}
                disabled={isValidating}
                className="w-full bg-green-600 text-white p-4 rounded-lg text-xl font-semibold hover:bg-green-700 mt-6 disabled:bg-gray-400"
              >
                {isValidating ? 'Validating...' : 'Get Started →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Helper: export data without requiring valid subscription
  const exportDataForLockedUser = () => {
    try {
      const records = JSON.parse(localStorage.getItem('epaFarmRecords') || '[]');
      const inventory = JSON.parse(localStorage.getItem('epaInventory') || '[]');
      const customChemicals = JSON.parse(localStorage.getItem('customChemicals') || '[]');
      const farmerName = localStorage.getItem('defaultFarmerName') || '';
      const propertyAddress = localStorage.getItem('propertyAddress') || '';

      if (records.length === 0 && inventory.length === 0) {
        alert('No data found to export.');
        return;
      }

      const dataToSave = {
        records,
        inventory,
        customChemicals,
        farmerName,
        propertyAddress,
        exportDate: new Date().toISOString(),
        exportReason: 'subscription-expired'
      };

      const blob = new Blob([JSON.stringify(dataToSave, null, 2)], { type: 'application/json' });
      const dateStr = new Date().toISOString().split('T')[0];
      const fileName = `AgriAdmin_Export_${dateStr}.json`;

      if (navigator.share && navigator.canShare) {
        const file = new File([blob], fileName, { type: 'application/json' });
        if (navigator.canShare({ files: [file] })) {
          navigator.share({ title: 'Agri Admin Export', files: [file] });
          return;
        }
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      alert('\u2713 Data exported! Keep this file safe — it contains all your records.');
    } catch (error) {
      alert('Error exporting data: ' + error.message);
    }
  };

  // Subscription invalid - locked
  if (subscriptionStatus && !subscriptionStatus.valid) {
    return (
      <div className="min-h-screen bg-red-50 p-4">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-lg shadow-lg p-8">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4">{'\ud83d\udd12'}</div>
              <h1 className="text-2xl font-bold text-red-800 mb-2">Subscription Required</h1>
              <p className="text-gray-600">{subscriptionStatus.message}</p>
            </div>

            <div className="space-y-3">
              <button
                onClick={() => window.open('https://billing.stripe.com/p/login/cNiaEXfiD3AK77P2HNdZ600', '_blank')}
                className="w-full bg-green-600 text-white p-4 rounded-lg text-xl font-semibold hover:bg-green-700"
              >
                Subscribe
              </button>

              <button
                onClick={exportDataForLockedUser}
                className="w-full bg-blue-600 text-white p-3 rounded-lg font-semibold hover:bg-blue-700"
              >
                {'\ud83d\udcbe'} Download My Data
              </button>

              <button
                onClick={() => {
                  localStorage.removeItem('subscriptionCache');
                  localStorage.removeItem('userEmail');
                  localStorage.removeItem('setupComplete');
                  localStorage.removeItem('defaultFarmerName');
                  localStorage.removeItem('propertyAddress');
                  localStorage.removeItem('lastAutoBackup');
                  localStorage.removeItem('lastSubscriptionCheck');
                  window.location.reload();
                }}
                className="w-full bg-gray-500 text-white p-3 rounded-lg"
              >
                Log Out & Try Different Email
              </button>
            </div>

            <div className="mt-6 text-center text-sm text-gray-600">
              <p>Email: {userEmail}</p>
              <p className="mt-2">Questions? agriadmi@gmail.com</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Still checking subscription (first load, no cache)
  if (subscriptionStatus === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="text-4xl mb-4">{'\ud83e\uddea'}</div>
          <p className="text-gray-600">Checking subscription...</p>
        </div>
      </div>
    );
  }

  // Valid subscription - load the real app
  return <AgriAdmin />;
}